/**
 * 교사·원장 학교·학년 정정 화면(읽기 + 정정). 반을 고르면 그 반 학생의 현재 학교·학년·상태와 최근 이력을 보여 주고,
 * 학교(학교 ID 목록, 학생 첫설정과 같은 /schools)와 학년을 고른 뒤 사유를 적어 정정한다. 사유는 필수이며 이력에 남는다.
 * 화면은 권한을 판단하지 않고 서버(담당 밖 404, 학생 403)의 응답만 보여 준다. 정정은 학습 기록(응답·채점·복습·교재 버전)을 바꾸지 않는다.
 * 진급 자동 실행은 학생이 접속할 때 서버가 하며, 이 화면은 조회만으로 진급을 일으키지 않는다.
 */
import { h, clear } from './dom.js';
import { currentGen, type Transport } from '../core/transport.js';

interface School { id: string; name: string; grades?: number[] }
interface Hist { source: string; schoolName: string; grade: number; academicYear: number | null; note: string | null; recordedAt: string }
interface Student { studentId: string; name: string; profile: { schoolId: string; schoolName: string; grade: number; status: string; academicYear: number } | null; history: Hist[] }

const STATUS: Record<string, string> = { active: '재학', 'pending-school': '진학 학교 선택 대기', graduated: '졸업' };
const SOURCE: Record<string, string> = {
  'first-registration': '최초 등록', 'self-update': '본인 변경', 'annual-promotion': '자동 진급', 'promotion-pending': '학교급 경계(학교 선택 대기)',
  graduated: '졸업', 'promotion-school-chosen': '진학 학교 선택', 'teacher-correction': '교사·원장 정정',
};
export const statusText = (s: string): string => STATUS[s] ?? s;
export const sourceText = (s: string): string => SOURCE[s] ?? s;

export function correctionFailText(status: number, body: Record<string, unknown>): string {
  if (status === 404) return '학생 또는 학교를 찾을 수 없거나 담당 범위 밖이에요.';
  if (status === 403) return '이 계정은 할 수 없는 작업이에요.';
  if (status === 401) return '다시 로그인해 주세요.';
  if (status === 400 && body['field'] === 'reason') return '정정 사유를 적어 주세요(300자 이내).';
  if (status === 400 && body['field'] === 'grade') return '그 학교에 없는 학년이에요.';
  return `정정하지 못했어요(${String(body['error'] ?? status)}). 학생 기록은 그대로예요.`;
}

export function mountProfileCorrection(t: Transport, classSel: HTMLSelectElement): HTMLElement {
  const gen = currentGen();
  const live = () => gen === currentGen();
  let schools: School[] = [];
  const msg = h('p', { role: 'status', 'data-testid': 'pc-message' });
  const list = h('div', { 'data-testid': 'pc-list' });
  let busy = false;

  void (async () => {
    const s = await t.request('GET', '/schools');
    if (live() && s.kind === 'http' && s.status === 200) schools = s.body['schools'] as School[];
  })();

  function row(st: Student): HTMLElement {
    const schoolSel = h('select', { 'data-testid': `pc-school-${st.studentId}`, 'aria-label': `${st.name} 학교` }, ...schools.map((x) => h('option', { value: x.id, ...(st.profile?.schoolId === x.id ? { selected: true } : {}) }, x.name)));
    const gradeSel = h('select', { 'data-testid': `pc-grade-${st.studentId}`, 'aria-label': `${st.name} 학년` });
    const fillGrades = () => {
      const sc = schools.find((x) => x.id === schoolSel.value);
      clear(gradeSel);
      for (const g of sc?.grades ?? [1, 2, 3]) gradeSel.append(h('option', { value: String(g), ...(st.profile?.grade === g && st.profile.schoolId === schoolSel.value ? { selected: true } : {}) }, `${g}학년`));
    };
    schoolSel.addEventListener('change', fillGrades);
    fillGrades();
    const reason = h('input', { 'data-testid': `pc-reason-${st.studentId}`, maxlength: '300', placeholder: '정정 사유(필수)', 'aria-label': `${st.name} 정정 사유` });
    const save = h('button', { type: 'button', class: 'secondary', 'data-testid': `pc-save-${st.studentId}`, onclick: async () => {
      if (busy) return; // 중복 제출 방지
      if (reason.value.trim() === '') { msg.textContent = '정정 사유를 적어 주세요.'; return; }
      busy = true; save.disabled = true; msg.textContent = '정정하는 중…';
      try {
        const r = await t.request('POST', `/admin/students/${encodeURIComponent(st.studentId)}/profile-correction`, { body: { schoolId: schoolSel.value, grade: Number(gradeSel.value), reason: reason.value.trim() } });
        if (!live()) return;
        if (r.kind !== 'http') { msg.textContent = '결과를 받지 못했어요. 다시 눌러도 같은 정정은 중복 기록되지 않아요.'; return; }
        if (r.status !== 200) { msg.textContent = correctionFailText(r.status, r.body); return; }
        msg.textContent = r.body['changed'] === false ? '이미 같은 학교·학년이라 바꾸지 않았어요.' : `${st.name} 학생의 학교·학년을 정정했어요. 이전 값은 이력에 남고 학습 기록은 그대로예요.`;
        await load();
      } finally { busy = false; save.disabled = false; }
    } }, '정정');
    return h('li', { 'data-testid': `pc-student-${st.studentId}` },
      h('strong', {}, st.name), ' ',
      h('span', { 'data-testid': `pc-current-${st.studentId}` }, st.profile === null ? '학교·학년 미설정(학생이 첫설정을 해야 해요)' : `${st.profile.schoolName} ${st.profile.grade}학년 · ${statusText(st.profile.status)}`),
      h('div', {}, schoolSel, ' ', gradeSel, ' ', reason, ' ', save),
      st.history.length === 0 ? null : h('details', {}, h('summary', {}, `최근 이력 ${st.history.length}건`),
        h('ul', { 'data-testid': `pc-history-${st.studentId}` }, ...st.history.map((x) => h('li', {}, `${sourceText(x.source)} · ${x.schoolName} ${x.grade}학년${x.note !== null ? ` · 사유: ${x.note}` : ''}`)))));
  }

  async function load(): Promise<void> {
    clear(list);
    if (classSel.value === '') return;
    const r = await t.request('GET', `/admin/classes/${encodeURIComponent(classSel.value)}/student-profiles`);
    if (!live()) return;
    if (r.kind !== 'http' || r.status !== 200) { msg.textContent = r.kind === 'http' && r.status === 404 ? '담당 범위 밖의 반이에요.' : '학생 목록을 불러오지 못했어요. 다시 반을 선택해 주세요.'; return; }
    const students = r.body['students'] as Student[];
    list.append(students.length === 0 ? h('p', { 'data-testid': 'pc-empty' }, '이 반에 학생이 없어요.') : h('ul', {}, ...students.map(row)));
  }
  classSel.addEventListener('change', () => { msg.textContent = ''; void load(); });

  return h('section', { 'data-testid': 'profile-correction' }, h('h2', {}, '학생 학교·학년 정정'),
    h('p', {}, '위에서 반을 고르면 그 반 학생의 학교·학년이 나와요. 전학이나 학년 예외가 있을 때만 정정하고, 사유는 꼭 적어 주세요. 이전 값은 이력에 남고 푼 기록은 바뀌지 않아요.'), msg, list);
}
